"use client";

import { Suspense, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "next/navigation";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslations, useLocale } from "next-intl";
import {
  ArrowRight,
  CheckCircle2,
  ChevronLeft,
  X,
  Globe,
  ShoppingBag,
  CalendarClock,
  HelpCircle,
  UtensilsCrossed,
  Scissors,
  Store,
  Briefcase,
} from "lucide-react";
import {
  contactWizardSchema,
  STEP_FIELDS,
  WIZARD_STEP_COUNT,
  PROJECT_TYPES,
  TIMELINES,
  BUDGETS,
  SECTORS,
  type ContactWizardInput,
  type ProjectType,
} from "@/lib/validators/contact-message";
import { submitContactMessage } from "@/actions/contact-messages";
import { ChoiceCard } from "./choice-card";

/**
 * Parcours « Parlons de votre projet » en PLEIN ÉCRAN, sur le modèle de
 * l'onboarding client (welcome-onboarding) : fond mint radial, orbes ambiants,
 * cascade d'apparition, rail de progression, chevron retour, clavier.
 *
 * La première question vit DANS la page (teaser) : dès la première action du
 * visiteur — choix d'un type de projet ou « Commencer » — l'expérience passe
 * en plein écran et enchaîne les étapes. Les étapes à choix unique avancent
 * toutes seules après sélection ; Échap ferme, l'état du formulaire survit à
 * la fermeture (react-hook-form garde les valeurs hors DOM).
 */

const PROJECT_ICONS = {
  vitrine: Globe,
  ecommerce: ShoppingBag,
  reservations: CalendarClock,
  unknown: HelpCircle,
} as const;

const SECTOR_ICONS = {
  restaurant: UtensilsCrossed,
  beaute: Scissors,
  commerce: Store,
  services: Briefcase,
  other: HelpCircle,
} as const;

/** Slug des pages `/solutions/<slug>` → secteur du parcours (`?sector=`). */
const SECTOR_FROM_SLUG: Record<string, (typeof SECTORS)[number]> = {
  restaurant: "restaurant",
  beaute: "beaute",
  ecommerce: "commerce",
  services: "services",
  "application-mobile": "other",
};

/**
 * Pré-remplissage depuis `?type=&sector=` (liens des pages métier). Isolé dans
 * sa propre frontière Suspense : `useSearchParams` sur une page prérendue fait
 * basculer en rendu client tout ce qui l'entoure jusqu'au Suspense le plus
 * proche — ici rien d'autre que lui, le teaser reste dans le HTML statique.
 */
function PrefillFromUrl({
  onPrefill,
}: {
  onPrefill: (projectType: ProjectType | undefined, sector: string | undefined) => void;
}) {
  const params = useSearchParams();
  const type = params.get("type");
  const sector = SECTOR_FROM_SLUG[params.get("sector") ?? ""];
  useEffect(() => {
    onPrefill(PROJECT_TYPES.find((p) => p === type), sector);
  }, [type, sector, onPrefill]);
  return null;
}

const inputClassName =
  "w-full rounded-lg border border-border bg-background/50 px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring";

/** Délai avant l'auto-avance d'un choix unique : l'état sélectionné se voit. */
const AUTO_ADVANCE_MS = 420;

/** Rang dans la cascade d'apparition (cf. welcome-onboarding : la propriété
 *  custom traverse les parents là où :nth-child ne le peut pas). */
const R = (i: number) => ({ ["--i"]: i }) as React.CSSProperties;

export function ContactWizard() {
  const t = useTranslations("contactForm");
  const locale = useLocale();

  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  // Garde anti-double envoi : le bouton peut être activé au clavier et à la
  // souris quasi simultanément, et React double les rendus en mode strict.
  const sending = useRef(false);
  const autoAdvance = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  const {
    register,
    control,
    handleSubmit,
    trigger,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<ContactWizardInput>({
    resolver: zodResolver(contactWizardSchema),
    defaultValues: { locale },
    mode: "onTouched",
  });

  const values = watch();

  // Le secteur est stocké sous son libellé traduit, comme le fait le choix à la main.
  const prefill = useCallback(
    (projectType: ProjectType | undefined, sector: string | undefined) => {
      if (projectType) setValue("projectType", projectType);
      if (sector) setValue("sector", t(`sector.${sector}`));
    },
    [setValue, t],
  );
  const isLast = step === WIZARD_STEP_COUNT - 1;

  const goNext = useCallback(async () => {
    // Les étapes de qualification n'ont aucun champ requis : `trigger` y répond
    // toujours vrai, ce qui rend le parcours franchissable de bout en bout sans
    // rien renseigner. Seule la dernière étape bloque réellement.
    const valid = await trigger([...STEP_FIELDS[step]]);
    if (valid) setStep((s) => Math.min(s + 1, WIZARD_STEP_COUNT - 1));
  }, [step, trigger]);

  // L'auto-avance part d'un setTimeout : sans ce relais, elle validerait
  // l'étape figée à la pose du timer, pas celle affichée au moment où il sonne.
  const goNextRef = useRef(goNext);
  useEffect(() => {
    goNextRef.current = goNext;
  });

  const back = useCallback(() => setStep((s) => Math.max(0, s - 1)), []);
  const close = useCallback(() => setOpen(false), []);

  function openAt(target: number) {
    setServerError(null);
    setStep(target);
    setOpen(true);
  }

  /** Choix unique : on laisse voir la sélection, puis on enchaîne. */
  function queueAdvance() {
    if (autoAdvance.current) clearTimeout(autoAdvance.current);
    autoAdvance.current = setTimeout(() => void goNextRef.current(), AUTO_ADVANCE_MS);
  }

  // Un retour ou un saut de rail annule l'auto-avance en attente, sinon elle
  // re-pousserait l'utilisateur en avant juste après son geste.
  useEffect(() => {
    return () => {
      if (autoAdvance.current) clearTimeout(autoAdvance.current);
    };
  }, [step]);

  // Plein écran : scroll de la page gelé, focus dans le dialogue, clavier
  // (Échap ferme ; flèches hors saisie ; Entrée avance sans soumettre).
  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    rootRef.current?.focus();

    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setOpen(false);
        return;
      }
      const el = e.target as HTMLElement | null;
      if (
        el instanceof HTMLInputElement ||
        el instanceof HTMLTextAreaElement ||
        el instanceof HTMLSelectElement
      ) {
        return;
      }
      if (e.key === "ArrowRight") void goNextRef.current();
      if (e.key === "ArrowLeft") back();
    }
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, back]);

  async function onSubmit(data: ContactWizardInput) {
    if (sending.current) return;
    sending.current = true;
    setServerError(null);

    try {
      const result = await submitContactMessage({ ...data, locale });
      if (result.error) {
        setServerError(
          typeof result.error === "string" ? result.error : t("errorGeneric"),
        );
        return;
      }
      setSubmitted(true);
    } catch {
      // Sans ce catch, un throw (coupure réseau, skew de déploiement — action
      // ID périmé) remontait à react-hook-form : bouton revenu à « Envoyer »,
      // AUCUN message, lead perdu sans que personne ne le sache.
      setServerError(t("errorGeneric"));
    } finally {
      sending.current = false;
    }
  }

  function onFormKeyDown(e: React.KeyboardEvent<HTMLFormElement>) {
    if (e.key !== "Enter" || isLast) return;
    if ((e.target as HTMLElement).tagName === "TEXTAREA") return;
    e.preventDefault();
    void goNext();
  }

  /* ── Teaser inline : la 1re question vit dans la page, toute action ouvre
        le plein écran. ── */

  if (submitted && !open) {
    return (
      <div className="glass-card rounded-2xl p-8">
        <div className="py-12 text-center">
          <CheckCircle2 className="mx-auto mb-4 h-12 w-12 text-primary" />
          <h2 className="mb-2 text-xl font-medium">{t("successTitle")}</h2>
          <p className="text-muted-foreground">{t("successText")}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Suspense fallback={null}>
        <PrefillFromUrl onPrefill={prefill} />
      </Suspense>
      <div className="glass-card rounded-2xl p-6 sm:p-8">
        <p className="mb-1 font-mono text-[10px] uppercase tracking-[0.3em] text-primary">
          {t("stepCounter", { current: 1, total: WIZARD_STEP_COUNT })}
        </p>
        <h2 className="text-2xl font-medium tracking-tight">
          {t("steps.0.title")}
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">{t("steps.0.hint")}</p>

        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          {PROJECT_TYPES.map((type) => (
            <ChoiceCard
              key={type}
              icon={PROJECT_ICONS[type]}
              label={t(`projectType.${type}.label`)}
              hint={t(`projectType.${type}.hint`)}
              active={values.projectType === type}
              onSelect={() => {
                setValue("projectType", type);
                openAt(1);
              }}
            />
          ))}
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
          <span className="text-xs text-muted-foreground">{t("teaserHint")}</span>
          <button
            type="button"
            onClick={() => openAt(values.projectType ? 1 : 0)}
            className="primary-button inline-flex items-center gap-1.5 rounded-lg px-6 py-3 text-sm font-medium"
          >
            {t("start")}
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* ── Plein écran — via portal : la page enveloppe ce composant dans un
            RevealOnScroll dont le transform ferait du wrapper le référent du
            `position:fixed`, encastrant l'overlay dans la colonne. Jamais rendu
            côté serveur (`open` ne devient vrai qu'au clic). ── */}
      {open &&
        createPortal(
        <div
          ref={rootRef}
          tabIndex={-1}
          role="dialog"
          aria-modal="true"
          aria-label={t("heading")}
          className="cw-root fixed inset-0 z-[80] flex flex-col text-foreground outline-none"
        >
          <style>{CW_CSS}</style>
          <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
            <span className="cw-orb cw-a" />
            <span className="cw-orb cw-b" />
            <span className="cw-orb cw-c" />
          </div>

          {submitted ? (
            <div className="relative z-[1] flex flex-1 flex-col items-center justify-center px-6 text-center">
              <CheckCircle2 className="cw-pop h-16 w-16 text-primary" />
              <h2 className="cw-reveal mt-6 text-3xl font-semibold tracking-tight" style={R(1)}>
                {t("successTitle")}
              </h2>
              <p className="cw-reveal mt-3 max-w-md text-muted-foreground" style={R(2)}>
                {t("successText")}
              </p>
              <button
                type="button"
                onClick={close}
                className="cw-reveal primary-button mt-10 rounded-lg px-8 py-3 text-sm font-medium"
                style={R(3)}
              >
                {t("close")}
              </button>
            </div>
          ) : (
            <form
              id="contact"
              onSubmit={handleSubmit(onSubmit)}
              onKeyDown={onFormKeyDown}
              className="relative z-[1] flex min-h-0 flex-1 flex-col"
            >
              {/* Honeypot — invisible pour un humain, tentant pour un robot. */}
              <div
                aria-hidden="true"
                style={{ position: "absolute", left: "-9999px", top: "-9999px" }}
              >
                <input type="text" tabIndex={-1} autoComplete="off" {...register("website")} />
              </div>

              <div className="flex items-center justify-between gap-3 px-4 py-3 sm:px-8 sm:py-5">
                <button
                  type="button"
                  onClick={back}
                  disabled={step === 0}
                  aria-label={t("back")}
                  className="glass-button flex h-10 w-10 items-center justify-center rounded-full transition-opacity disabled:opacity-30"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-primary">
                  {t("stepCounter", { current: step + 1, total: WIZARD_STEP_COUNT })}
                </p>
                <button
                  type="button"
                  onClick={close}
                  aria-label={t("close")}
                  className="glass-button flex h-10 w-10 items-center justify-center rounded-full"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* `key` remonte le bloc à chaque étape : la cascade rejoue. */}
              <div className="min-h-0 flex-1 overflow-y-auto px-4 sm:px-8">
                <div key={step} className="mx-auto w-full max-w-2xl py-6 sm:py-10">
                  <h2
                    className="cw-reveal text-3xl font-semibold tracking-tight sm:text-4xl"
                    style={R(0)}
                  >
                    {t(`steps.${step}.title`)}
                  </h2>
                  <p
                    className="cw-reveal mt-2 text-sm text-muted-foreground sm:text-base"
                    style={R(1)}
                  >
                    {t(`steps.${step}.hint`)}
                  </p>

                  <div className="cw-reveal mt-8" style={R(2)}>
                    {step === 0 && (
                      <Controller
                        control={control}
                        name="projectType"
                        render={({ field }) => (
                          <div className="grid gap-3 sm:grid-cols-2">
                            {PROJECT_TYPES.map((type) => (
                              <ChoiceCard
                                key={type}
                                icon={PROJECT_ICONS[type]}
                                label={t(`projectType.${type}.label`)}
                                hint={t(`projectType.${type}.hint`)}
                                active={field.value === type}
                                onSelect={() => {
                                  const deselect = field.value === type;
                                  field.onChange(deselect ? undefined : type);
                                  if (!deselect) queueAdvance();
                                }}
                              />
                            ))}
                          </div>
                        )}
                      />
                    )}

                    {step === 1 && (
                      <div className="space-y-6">
                        <Controller
                          control={control}
                          name="sector"
                          render={({ field }) => (
                            <div>
                              <span className="mb-2 block text-sm font-medium">
                                {t("sectorLabel")}
                              </span>
                              <div className="grid gap-3 sm:grid-cols-2">
                                {SECTORS.map((sector) => {
                                  const label = t(`sector.${sector}`);
                                  return (
                                    <ChoiceCard
                                      key={sector}
                                      icon={SECTOR_ICONS[sector]}
                                      label={label}
                                      active={field.value === label}
                                      onSelect={() =>
                                        field.onChange(field.value === label ? "" : label)
                                      }
                                    />
                                  );
                                })}
                              </div>
                              {/* Champ libre : les suggestions ne couvrent pas tout, et une
                                  activité hors des cases ne doit pas rester sans réponse. */}
                              <input
                                type="text"
                                className={`${inputClassName} mt-3`}
                                placeholder={t("sectorPlaceholder")}
                                value={field.value ?? ""}
                                onChange={(e) => field.onChange(e.target.value)}
                              />
                            </div>
                          )}
                        />

                        <div>
                          <label className="mb-2 block text-sm font-medium">
                            {t("locationLabel")}
                          </label>
                          <input
                            type="text"
                            className={inputClassName}
                            placeholder={t("locationPlaceholder")}
                            {...register("location")}
                          />
                        </div>
                      </div>
                    )}

                    {step === 2 && (
                      <div className="space-y-6">
                        <Controller
                          control={control}
                          name="timeline"
                          render={({ field }) => (
                            <div>
                              <span className="mb-2 block text-sm font-medium">
                                {t("timelineLabel")}
                              </span>
                              <div className="grid gap-3 sm:grid-cols-2">
                                {TIMELINES.map((value) => (
                                  <ChoiceCard
                                    key={value}
                                    label={t(`timeline.${value}`)}
                                    active={field.value === value}
                                    onSelect={() =>
                                      field.onChange(field.value === value ? undefined : value)
                                    }
                                  />
                                ))}
                              </div>
                            </div>
                          )}
                        />

                        <Controller
                          control={control}
                          name="hasWebsite"
                          render={({ field }) => (
                            <div>
                              <span className="mb-2 block text-sm font-medium">
                                {t("hasWebsiteLabel")}
                              </span>
                              <div className="grid gap-3 sm:grid-cols-2">
                                {[true, false].map((value) => (
                                  <ChoiceCard
                                    key={String(value)}
                                    label={value ? t("hasWebsiteYes") : t("hasWebsiteNo")}
                                    active={field.value === value}
                                    onSelect={() =>
                                      field.onChange(field.value === value ? undefined : value)
                                    }
                                  />
                                ))}
                              </div>
                            </div>
                          )}
                        />
                      </div>
                    )}

                    {step === 3 && (
                      <Controller
                        control={control}
                        name="budget"
                        render={({ field }) => (
                          <div className="grid gap-3 sm:grid-cols-2">
                            {BUDGETS.map((value) => (
                              <ChoiceCard
                                key={value}
                                label={t(`budget.${value}`)}
                                active={field.value === value}
                                onSelect={() => {
                                  const deselect = field.value === value;
                                  field.onChange(deselect ? undefined : value);
                                  if (!deselect) queueAdvance();
                                }}
                              />
                            ))}
                          </div>
                        )}
                      />
                    )}

                    {step === 4 && (
                      <div className="space-y-5">
                        {/* Récapitulatif : le projet du visiteur prend forme sous ses yeux
                            avant qu'il ne laisse ses coordonnées. */}
                        {(values.projectType || values.sector || values.location) && (
                          <div className="rounded-xl border border-primary/20 bg-primary/[0.05] px-4 py-3">
                            <p className="mb-1 font-mono text-[10px] uppercase tracking-[0.2em] text-primary">
                              {t("recapTitle")}
                            </p>
                            <p className="text-sm text-card-foreground">
                              {[
                                values.projectType &&
                                  t(`projectType.${values.projectType}.label`),
                                values.sector,
                                values.location,
                              ]
                                .filter(Boolean)
                                .join(" · ")}
                            </p>
                          </div>
                        )}

                        <div className="grid gap-5 sm:grid-cols-2">
                          <div>
                            <label className="mb-2 block text-sm font-medium">
                              {t("name")}
                            </label>
                            <input
                              type="text"
                              className={inputClassName}
                              placeholder={t("namePlaceholder")}
                              {...register("name")}
                            />
                            {errors.name && (
                              <p className="mt-1 text-sm text-destructive">
                                {errors.name.message}
                              </p>
                            )}
                          </div>
                          <div>
                            <label className="mb-2 block text-sm font-medium">
                              {t("email")}
                            </label>
                            <input
                              type="email"
                              className={inputClassName}
                              placeholder={t("emailPlaceholder")}
                              {...register("email")}
                            />
                            {errors.email && (
                              <p className="mt-1 text-sm text-destructive">
                                {errors.email.message}
                              </p>
                            )}
                          </div>
                        </div>

                        <div>
                          <label className="mb-2 block text-sm font-medium">
                            {t("phone")}{" "}
                            <span className="text-muted-foreground">{t("optional")}</span>
                          </label>
                          <input
                            type="tel"
                            className={inputClassName}
                            placeholder={t("phonePlaceholder")}
                            {...register("phone")}
                          />
                        </div>

                        <div>
                          <label className="mb-2 block text-sm font-medium">
                            {t("message")}{" "}
                            <span className="text-muted-foreground">{t("optional")}</span>
                          </label>
                          <textarea
                            rows={4}
                            className={`${inputClassName} resize-none`}
                            placeholder={t("messagePlaceholder")}
                            {...register("message")}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {serverError && (
                    <p className="mt-6 text-sm text-destructive">{serverError}</p>
                  )}
                </div>
              </div>

              <div className="px-4 pb-4 pt-2 sm:px-8 sm:pb-6">
                <div className="mx-auto flex w-full max-w-2xl flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div
                    className="flex flex-1 gap-2 sm:max-w-[300px]"
                    role="tablist"
                    aria-label={t("stepLabel")}
                  >
                    {Array.from({ length: WIZARD_STEP_COUNT }).map((_, k) => (
                      <button
                        key={k}
                        type="button"
                        role="tab"
                        aria-selected={k === step}
                        aria-label={t("stepCounter", {
                          current: k + 1,
                          total: WIZARD_STEP_COUNT,
                        })}
                        onClick={() => setStep(k)}
                        className={`h-1 flex-1 overflow-hidden rounded-full bg-white/10 ${
                          k <= step ? "cw-seg-on" : ""
                        }`}
                      >
                        <i className="cw-fill" />
                      </button>
                    ))}
                  </div>

                  <div className="flex items-center justify-between gap-4 sm:justify-end">
                    {!isLast && (
                      <button
                        type="button"
                        onClick={() => void goNext()}
                        className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                      >
                        {t("skip")}
                      </button>
                    )}

                    {isLast ? (
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        data-rl-track="contact:send"
                        className="primary-button rounded-lg px-6 py-3 text-sm font-medium text-foreground disabled:opacity-50"
                      >
                        {isSubmitting ? t("submitting") : t("submit")}
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => void goNext()}
                        // Une action par étape : on voit à laquelle on abandonne.
                        data-rl-track={`contact:next_${step + 1}`}
                        className="primary-button inline-flex items-center gap-1.5 rounded-lg px-6 py-3 text-sm font-medium text-foreground"
                      >
                        {t("next")}
                        <ArrowRight className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </form>
          )}
        </div>,
        document.body,
      )}
    </>
  );
}

/* Même parti pris que l'onboarding client (welcome-onboarding) : le CSS vit
   avec le composant, préfixé, et l'état de base reste visible pour que
   prefers-reduced-motion ne laisse jamais rien d'invisible. */
const CW_CSS = `
.cw-root{background:radial-gradient(120% 120% at 80% -10%,#12241d 0%,#0f1f1b 40%,#0c1513 100%);animation:cw-in .35s ease both}
@keyframes cw-in{from{opacity:0}to{opacity:1}}
.cw-orb{position:absolute;border-radius:50%;filter:blur(70px);opacity:.5;background:radial-gradient(circle,rgba(76,230,185,.10),transparent 70%)}
.cw-a{width:560px;height:560px;left:-120px;top:-140px;animation:cw-float 13s cubic-bezier(.22,1,.36,1) infinite}
.cw-b{width:680px;height:680px;right:-200px;bottom:-260px;background:radial-gradient(circle,rgba(59,196,160,.12),transparent 70%);animation:cw-float 17s cubic-bezier(.22,1,.36,1) infinite reverse}
.cw-c{width:340px;height:340px;left:44%;top:38%;opacity:.28;animation:cw-float 21s cubic-bezier(.22,1,.36,1) infinite}
@keyframes cw-float{0%,100%{transform:translate(0,0)}50%{transform:translate(26px,-30px)}}
.cw-reveal{animation:cw-up .6s cubic-bezier(.22,1,.36,1) both;animation-delay:calc(80ms + min(var(--i,0),8) * 70ms)}
@keyframes cw-up{from{opacity:0;transform:translateY(18px)}to{opacity:1;transform:none}}
.cw-pop{animation:cw-pop .5s cubic-bezier(.22,1,.36,1) both}
@keyframes cw-pop{0%{opacity:0;transform:scale(.6)}60%{opacity:1;transform:scale(1.08)}100%{opacity:1;transform:scale(1)}}
.cw-fill{display:block;height:100%;width:0;background:linear-gradient(90deg,#4CE6B9,#3BC4A0);border-radius:inherit;transition:width .5s cubic-bezier(.22,1,.36,1)}
.cw-seg-on .cw-fill{width:100%}
@media(prefers-reduced-motion:reduce){
  .cw-root,.cw-root *{animation:none!important;animation-delay:0s!important}
  .cw-orb{display:none}
}
`;
